import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';

import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { useAuth } from '@/context/AuthContext';
import { Button, FormCheckbox, FormInput, FormSelect, Modal } from '@/components/UI';

import {
    type ResourceDirectGrant,
    type ResourceGrantRole,
    type ShareableResourceType,
    type UsersRow,
    ResourceGrantRole as ResourceGrantRoleValues
} from '@einhaus/project314-types';

type ShareResourceModalProps = {
    isOpen: boolean;
    onClose: () => void;
    resourceLabel: string;
    resourcePk: number;
    resourceType: ShareableResourceType;
};

type ShareTarget = Pick<UsersRow, 'email' | 'id' | 'type'>;
type ShareTargetPayload = ShareTarget[] | { json?: ShareTarget[] } | undefined;

type ActiveTab = 'invite' | 'publish';

const normalizeShareTargets = (payload: ShareTargetPayload): ShareTarget[] => {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (payload?.json && Array.isArray(payload.json)) {
        return payload.json;
    }

    return [];
};

const roleDescriptionForType = (resourceType: ShareableResourceType, role: ResourceGrantRole): string => {
    if (role === ResourceGrantRoleValues.Viewer) {
        return 'Can view but not modify or use';
    }

    if (resourceType === 'tradingProfile' && role === ResourceGrantRoleValues.Consumer) {
        return 'Can use live in portfolios without editing or copying';
    }

    if (resourceType === 'tradingProfile' && role === ResourceGrantRoleValues.Copier) {
        return 'Can create an independent copy, but cannot use this live profile';
    }

    if (resourceType === 'tradingStrategy' && role === ResourceGrantRoleValues.Consumer) {
        return 'Can use in their own portfolios';
    }

    if (role === ResourceGrantRoleValues.Consumer) {
        return 'Can use in their own strategies';
    }

    return 'Can view and modify';
};

const roleOptionsByType = (resourceType: ShareableResourceType) => {
    if (resourceType === 'scorecard') {
        return [{ value: ResourceGrantRoleValues.Viewer, label: 'View Only' }];
    }

    if (resourceType === 'tradingProfile') {
        return [
            { value: ResourceGrantRoleValues.Viewer, label: 'View Only' },
            { value: ResourceGrantRoleValues.Consumer, label: 'Can Use' },
            { value: ResourceGrantRoleValues.Copier, label: 'Can Copy' },
            { value: ResourceGrantRoleValues.Editor, label: 'Can Edit' }
        ];
    }

    return [
        { value: ResourceGrantRoleValues.Viewer, label: 'View Only' },
        { value: ResourceGrantRoleValues.Consumer, label: 'Can Use' },
        { value: ResourceGrantRoleValues.Editor, label: 'Can Edit' }
    ];
};

const defaultRoleForType = (resourceType: ShareableResourceType): ResourceGrantRole =>
    resourceType === 'scorecard' ? ResourceGrantRoleValues.Viewer : ResourceGrantRoleValues.Consumer;

const supportsDependencySharing = (resourceType: ShareableResourceType): boolean =>
    resourceType === 'tradingStrategy' || resourceType === 'tradingProfile';

const ROLE_DISPLAY: Record<string, string> = {
    [ResourceGrantRoleValues.Viewer]: 'View only',
    [ResourceGrantRoleValues.Consumer]: 'Can use',
    [ResourceGrantRoleValues.Copier]: 'Can copy',
    [ResourceGrantRoleValues.Editor]: 'Can edit'
};

const roleDisplayForType = (_resourceType: ShareableResourceType, role: ResourceGrantRole): string => ROLE_DISPLAY[role] ?? role;

const dependencySharingLabelForType = (resourceType: ShareableResourceType): string => {
    if (resourceType === 'tradingStrategy') {
        return 'Also share linked option-buying rules and the active scorecard';
    }

    return 'Also share linked option-buying rules';
};

const allowUseLabelForType = (resourceType: ShareableResourceType): string => {
    if (resourceType === 'tradingProfile') {
        return 'Allow others to use or copy directly';
    }

    if (resourceType === 'tradingStrategy') {
        return 'Allow direct portfolio use without forking';
    }

    return 'Allow direct use without forking';
};

const UserInitial = ({ email }: { email: string }) => {
    const initial = email.charAt(0).toUpperCase();

    return (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-700 text-xs font-semibold text-zinc-200">
            {initial}
        </div>
    );
};

const TabButton = ({ active, children, onClick }: { active: boolean; children: React.ReactNode; onClick: () => void }) => (
    <button
        className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
            active ? 'bg-p314-blue text-white' : 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200'
        }`}
        type="button"
        onClick={onClick}
    >
        {children}
    </button>
);

type ContentProps = Omit<ShareResourceModalProps, 'isOpen'>;

const ShareResourceModalContent = ({ resourceLabel, resourcePk, resourceType }: Omit<ContentProps, 'onClose'>) => {
    const queryClient = useQueryClient();
    const { user, sessionPolicy } = useAuth();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const [activeTab, setActiveTab] = useState<ActiveTab>('invite');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedTargetUserId, setSelectedTargetUserId] = useState<number | null>(null);
    const [selectedTargetEmail, setSelectedTargetEmail] = useState('');
    const [selectedRole, setSelectedRole] = useState<ResourceGrantRole>(() => defaultRoleForType(resourceType));
    const [includeDependencies, setIncludeDependencies] = useState(supportsDependencySharing(resourceType));
    const [publishTitle, setPublishTitle] = useState(resourceLabel);
    const [publishSummary, setPublishSummary] = useState('');
    const [publishTags, setPublishTags] = useState('');
    const [allowFork, setAllowFork] = useState(true);
    const [allowUse, setAllowUse] = useState(resourceType !== 'scorecard');

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
        }, 300);

        return () => window.clearTimeout(timeoutId);
    }, [searchTerm]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const accessSummaryQuery = useQuery({
        ...trpc.resourceAccess.getAccessSummary.queryOptions({ resourcePk, resourceType }),
        enabled: true
    });

    const directGrantsQuery = useQuery({
        ...trpc.resourceAccess.listDirectGrants.queryOptions({ resourcePk, resourceType }),
        enabled: accessSummaryQuery.data?.ownedByUser === true
    });

    const shareTargetsQuery = useQuery({
        ...trpc.resourceAccess.searchShareTargets.queryOptions({
            term: debouncedSearchTerm,
            limit: 8
        }),
        enabled: accessSummaryQuery.data?.ownedByUser === true && debouncedSearchTerm.length >= 2 && !selectedTargetUserId,
        placeholderData: keepPreviousData
    });

    const publicationsQuery = useQuery({
        ...trpc.resourceAccess.listPublications.queryOptions({
            ownerUserId: user?.id,
            resourceType
        }),
        enabled: accessSummaryQuery.data?.ownedByUser === true && !!user?.id
    });

    const grantMutation = useTrpcMutation(
        trpc.resourceAccess.grantUserAccess.mutationOptions({
            onSuccess: async () => {
                await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: trpc.resourceAccess.listDirectGrants.queryOptions({ resourcePk, resourceType }).queryKey
                    }),
                    queryClient.invalidateQueries({
                        queryKey: trpc.resourceAccess.listAccessibleResources.queryOptions({ resourceType }).queryKey
                    })
                ]);
            }
        }),
        { successMessage: 'Access granted', fallbackErrorMessage: 'Failed to grant access' }
    );

    const revokeMutation = useTrpcMutation(
        trpc.resourceAccess.revokeUserAccess.mutationOptions({
            onSuccess: async () => {
                await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: trpc.resourceAccess.listDirectGrants.queryOptions({ resourcePk, resourceType }).queryKey
                    }),
                    queryClient.invalidateQueries({
                        queryKey: trpc.resourceAccess.listAccessibleResources.queryOptions({ resourceType }).queryKey
                    })
                ]);
            }
        }),
        { successMessage: 'Access revoked', fallbackErrorMessage: 'Failed to revoke access' }
    );

    const publishMutation = useTrpcMutation(
        trpc.resourceAccess.publishVersion.mutationOptions({
            onSuccess: async () => {
                if (!user?.id) {
                    return;
                }

                await queryClient.invalidateQueries({
                    queryKey: trpc.resourceAccess.listPublications.queryOptions({ ownerUserId: user.id, resourceType }).queryKey
                });
            }
        }),
        { successMessage: 'Published to library', fallbackErrorMessage: 'Failed to publish resource' }
    );

    const withdrawMutation = useTrpcMutation(
        trpc.resourceAccess.withdrawPublication.mutationOptions({
            onSuccess: async () => {
                if (!user?.id) {
                    return;
                }

                await queryClient.invalidateQueries({
                    queryKey: trpc.resourceAccess.listPublications.queryOptions({ ownerUserId: user.id, resourceType }).queryKey
                });
            }
        }),
        { successMessage: 'Publication withdrawn', fallbackErrorMessage: 'Failed to withdraw publication' }
    );

    const currentPublication = useMemo(() => {
        const publications = publicationsQuery.data ?? [];

        return publications
            .filter((publication) => publication.resourcePk === resourcePk && publication.listingStatus === 'listed')
            .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt))[0];
    }, [publicationsQuery.data, resourcePk]);

    const shareTargets = useMemo(() => {
        if (selectedTargetUserId || debouncedSearchTerm.length < 2) {
            return [];
        }

        return normalizeShareTargets(shareTargetsQuery.data);
    }, [debouncedSearchTerm, selectedTargetUserId, shareTargetsQuery.data]);

    const showDropdown = isDropdownOpen && debouncedSearchTerm.length >= 2 && !selectedTargetUserId;
    const canManageSharing = accessSummaryQuery.data?.ownedByUser === true && sessionPolicy.canManageSharedResources;
    const roleOptions = useMemo(() => roleOptionsByType(resourceType), [resourceType]);
    const directGrants = (directGrantsQuery.data ?? []) as ResourceDirectGrant[];

    const clearSelection = useCallback(() => {
        setSelectedTargetUserId(null);
        setSelectedTargetEmail('');
        setSearchTerm('');
        setDebouncedSearchTerm('');
        setIsDropdownOpen(false);
        searchInputRef.current?.focus();
    }, []);

    const handleGrant = async () => {
        if (!selectedTargetUserId) {
            return;
        }

        await grantMutation.mutateAsync({
            resourcePk,
            resourceType,
            role: selectedRole,
            targetUserId: selectedTargetUserId,
            includeDependencies: supportsDependencySharing(resourceType) ? includeDependencies : undefined
        });

        clearSelection();
        setSelectedRole(defaultRoleForType(resourceType));
    };

    const handlePublish = async () => {
        const trimmedTitle = publishTitle.trim();

        if (!trimmedTitle) {
            return;
        }

        await publishMutation.mutateAsync({
            resourcePk,
            resourceType,
            title: trimmedTitle,
            summary: publishSummary.trim() || undefined,
            tags: publishTags
                .split(',')
                .map((tag) => tag.trim())
                .filter(Boolean),
            allowFork,
            allowUse: resourceType !== 'scorecard' ? allowUse : false
        });
    };

    if (!canManageSharing) {
        return (
            <div className="flex items-center gap-3 rounded-md border border-zinc-700 bg-zinc-900/50 p-4">
                <svg className="h-5 w-5 shrink-0 text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                    />
                </svg>
                <p className="text-sm text-zinc-300">
                    {accessSummaryQuery.data?.ownedByUser === true
                        ? 'This support session does not include the resource-sharing write scope.'
                        : 'Only the owner of this resource can manage sharing and publishing settings.'}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Tab bar */}
            <div className="flex gap-1 rounded-lg bg-zinc-900/60 p-1">
                <TabButton active={activeTab === 'invite'} onClick={() => setActiveTab('invite')}>
                    Invite People
                </TabButton>
                <TabButton active={activeTab === 'publish'} onClick={() => setActiveTab('publish')}>
                    Publish to Library
                    {currentPublication && <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />}
                </TabButton>
            </div>

            {/* Tab content */}
            <div className="max-h-[calc(90vh-180px)] overflow-y-auto pr-1">
                {activeTab === 'invite' ? (
                    <div className="space-y-5">
                        {/* User search + role selection */}
                        <div>
                            {selectedTargetUserId ? (
                                <div className="space-y-3">
                                    <span className="block text-sm font-medium text-zinc-200">Invite</span>
                                    <div className="flex items-center gap-2 rounded-md border border-zinc-600 bg-zinc-800/60 px-3 py-2">
                                        <UserInitial email={selectedTargetEmail} />
                                        <span className="truncate text-sm text-zinc-100">{selectedTargetEmail}</span>
                                        <button
                                            className="ml-auto shrink-0 rounded p-0.5 text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-200"
                                            title="Clear selection"
                                            type="button"
                                            onClick={clearSelection}
                                        >
                                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path
                                                    d="M6 18L18 6M6 6l12 12"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="w-40 shrink-0">
                                            <FormSelect
                                                noMargin
                                                options={roleOptions}
                                                value={selectedRole}
                                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                                    setSelectedRole(e.target.value as ResourceGrantRole)
                                                }
                                            />
                                        </div>
                                        <p className="min-w-0 flex-1 text-xs text-zinc-500">
                                            {roleDescriptionForType(resourceType, selectedRole)}
                                        </p>
                                        <Button
                                            className="shrink-0"
                                            disabled={grantMutation.isPending}
                                            variant="primary"
                                            onClick={handleGrant}
                                        >
                                            {grantMutation.isPending ? 'Inviting...' : 'Invite'}
                                        </Button>
                                    </div>

                                    {supportsDependencySharing(resourceType) && (
                                        <FormCheckbox
                                            checked={includeDependencies}
                                            label={dependencySharingLabelForType(resourceType)}
                                            onChange={(e) => setIncludeDependencies(e.target.checked)}
                                        />
                                    )}
                                </div>
                            ) : (
                                <div className="relative" ref={dropdownRef}>
                                    <label className="mb-1 block text-sm font-medium text-zinc-200" htmlFor="share-user-search">
                                        Invite people by email
                                    </label>
                                    <div className="relative">
                                        <svg
                                            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                            />
                                        </svg>
                                        <input
                                            autoComplete="off"
                                            className="h-[38px] w-full rounded-md border border-[#444] bg-[#1E1E1E] py-2 pl-10 pr-3 text-zinc-300 placeholder-[#7c8083] focus:border-[#888] focus:outline-none focus:ring-1 focus:ring-[#888]"
                                            id="share-user-search"
                                            placeholder="Search by email address..."
                                            ref={searchInputRef}
                                            type="text"
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                setSelectedTargetUserId(null);
                                                setSelectedTargetEmail('');
                                                setIsDropdownOpen(true);
                                            }}
                                            onFocus={() => {
                                                if (searchTerm.length >= 2) {
                                                    setIsDropdownOpen(true);
                                                }
                                            }}
                                        />
                                    </div>

                                    {showDropdown && (
                                        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-zinc-600 bg-[#1a1a1a] shadow-lg shadow-black/40">
                                            {shareTargetsQuery.isFetching && shareTargets.length === 0 ? (
                                                <div className="flex items-center gap-2 px-3 py-3 text-sm text-zinc-400">
                                                    <svg className="h-4 w-4 animate-spin text-zinc-500" fill="none" viewBox="0 0 24 24">
                                                        <circle
                                                            className="opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            stroke="currentColor"
                                                            strokeWidth="4"
                                                        />
                                                        <path
                                                            className="opacity-75"
                                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                            fill="currentColor"
                                                        />
                                                    </svg>
                                                    Searching...
                                                </div>
                                            ) : shareTargets.length > 0 ? (
                                                <div className="max-h-56 overflow-y-auto">
                                                    {shareTargets.map((target) => (
                                                        <button
                                                            className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-zinc-800"
                                                            key={target.id}
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedTargetUserId(target.id);
                                                                setSelectedTargetEmail(target.email);
                                                                setSearchTerm(target.email);
                                                                setIsDropdownOpen(false);
                                                            }}
                                                        >
                                                            <UserInitial email={target.email} />
                                                            <div className="min-w-0 flex-1">
                                                                <div className="truncate text-zinc-200">{target.email}</div>
                                                            </div>
                                                            <span className="shrink-0 rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-zinc-500">
                                                                {target.type}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : shareTargetsQuery.isSuccess ? (
                                                <div className="px-3 py-3 text-sm text-zinc-500">
                                                    No users found matching &ldquo;{debouncedSearchTerm}&rdquo;
                                                </div>
                                            ) : null}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="border-t border-zinc-800" />

                        {/* People with access */}
                        <div>
                            <h4 className="mb-3 text-sm font-medium text-zinc-300">People with access</h4>

                            {directGrantsQuery.isLoading ? (
                                <div className="py-4 text-center text-sm text-zinc-500">Loading...</div>
                            ) : directGrants.length === 0 ? (
                                <div className="rounded-md border border-dashed border-zinc-700 px-4 py-6 text-center">
                                    <svg
                                        className="mx-auto mb-2 h-8 w-8 text-zinc-600"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={1.5}
                                        />
                                    </svg>
                                    <p className="text-sm text-zinc-500">Not shared with anyone yet</p>
                                    <p className="mt-1 text-xs text-zinc-600">Use the search above to invite people</p>
                                </div>
                            ) : (
                                <div className="space-y-1.5">
                                    {directGrants.map((grant) => (
                                        <div
                                            className="group flex items-center gap-3 rounded-md px-3 py-2 transition-colors hover:bg-zinc-800/50"
                                            key={grant.id}
                                        >
                                            <UserInitial email={grant.targetUserEmail ?? `U${grant.principalId}`} />
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate text-sm text-zinc-200">
                                                    {grant.targetUserEmail ?? `User #${grant.principalId}`}
                                                </div>
                                                <div className="text-xs text-zinc-500">{roleDisplayForType(resourceType, grant.role)}</div>
                                            </div>
                                            <button
                                                className="shrink-0 rounded px-2 py-1 text-xs text-zinc-500 opacity-0 transition-all hover:bg-red-900/30 hover:text-red-400 group-hover:opacity-100"
                                                disabled={revokeMutation.isPending}
                                                title="Remove access"
                                                type="button"
                                                onClick={() =>
                                                    revokeMutation.mutate({
                                                        resourcePk,
                                                        resourceType,
                                                        targetUserId: grant.principalId
                                                    })
                                                }
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {currentPublication ? (
                            <div className="space-y-4">
                                <div className="rounded-md border border-emerald-800/50 bg-emerald-950/30 p-4">
                                    <div className="flex items-start gap-3">
                                        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-900/50 text-emerald-400">
                                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                                            </svg>
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium text-emerald-200">{currentPublication.title}</div>
                                            <div className="mt-0.5 text-xs text-emerald-300/60">
                                                Published {currentPublication.publishedAt}
                                            </div>
                                            {currentPublication.summary && (
                                                <div className="mt-2 text-sm text-zinc-300">{currentPublication.summary}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <p className="text-xs text-zinc-500">
                                    This resource is live in the community library. Other users can discover and use it. Withdrawing will
                                    remove it from the library but won&apos;t affect users who already forked it.
                                </p>

                                <div className="flex justify-end">
                                    <Button
                                        disabled={withdrawMutation.isPending}
                                        size="sm"
                                        variant="destructive"
                                        onClick={() => withdrawMutation.mutate({ publicationId: currentPublication.id })}
                                    >
                                        {withdrawMutation.isPending ? 'Withdrawing...' : 'Withdraw from Library'}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <p className="text-sm text-zinc-400">
                                    Make this available in the community library for all users to discover. Publishing creates a
                                    snapshot&mdash;your working copy stays independent.
                                </p>

                                <FormInput label="Title" value={publishTitle} onChange={(e) => setPublishTitle(e.target.value)} />

                                <div>
                                    <label className="mb-1 block text-sm font-medium text-zinc-200" htmlFor="publication-summary">
                                        Description
                                    </label>
                                    <textarea
                                        className="min-h-[80px] w-full rounded-md border border-[#444] bg-[#1E1E1E] px-3 py-2 text-sm text-zinc-300 placeholder-[#7c8083] focus:border-[#888] focus:outline-none focus:ring-1 focus:ring-[#888]"
                                        id="publication-summary"
                                        placeholder="Briefly describe what this does and when to use it..."
                                        value={publishSummary}
                                        onChange={(e) => setPublishSummary(e.target.value)}
                                    />
                                </div>

                                <FormInput
                                    helpText="Separate with commas (e.g. momentum, swing, breakout)"
                                    label="Tags"
                                    placeholder="Add tags..."
                                    value={publishTags}
                                    onChange={(e) => setPublishTags(e.target.value)}
                                />

                                <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-3">
                                    <h5 className="mb-2 text-xs font-medium uppercase tracking-wider text-zinc-500">Permissions</h5>
                                    <div className="space-y-2">
                                        <FormCheckbox
                                            checked={allowFork}
                                            label="Allow others to fork (create their own copy)"
                                            onChange={(e) => setAllowFork(e.target.checked)}
                                        />
                                        {resourceType !== 'scorecard' && (
                                            <FormCheckbox
                                                checked={allowUse}
                                                label={allowUseLabelForType(resourceType)}
                                                onChange={(e) => setAllowUse(e.target.checked)}
                                            />
                                        )}
                                    </div>
                                </div>

                                <div className="flex justify-end">
                                    <Button
                                        disabled={!publishTitle.trim() || publishMutation.isPending}
                                        variant="primary"
                                        onClick={handlePublish}
                                    >
                                        {publishMutation.isPending ? 'Publishing...' : 'Publish to Library'}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const ShareResourceModal = ({ isOpen, onClose, resourceLabel, resourcePk, resourceType }: ShareResourceModalProps) => (
    <Modal
        centered={false}
        className="max-h-[90vh] overflow-hidden"
        isOpen={isOpen}
        size="lg"
        title={`Share "${resourceLabel}"`}
        onClose={onClose}
    >
        {isOpen && (
            <ShareResourceModalContent
                key={`${resourcePk}-${resourceType}`}
                resourceLabel={resourceLabel}
                resourcePk={resourcePk}
                resourceType={resourceType}
            />
        )}
    </Modal>
);

export default ShareResourceModal;
